import { call, put, takeLatest } from 'redux-saga/effects';
import Swal from 'sweetalert2';
import * as types from './actionTypes';
import { post } from 'helpers/api_helper';

// Employee Dropdown
function* fetchEmployeeListSaga(action) {
  try {
    const response = yield call(post, process.env.REACT_APP_GET_MARKER_EMPLOYEE_LIST, action.payload);

    if (!response || response.status !== 'success') {
      throw new Error(response?.message || 'Failed to fetch employee list!');
    }

    yield put({ type: types.FETCH_EMPLOYEE_LIST_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire('Error', error.message, 'error');
    yield put({ type: types.FETCH_EMPLOYEE_LIST_FAILURE, payload: error });
  }
}

// Leave Type
function* fetchLeaveTypeSaga(action) {
  const { unit_id } = action.payload;
  try {
    const formData = new FormData();
    formData.append('unit_id', unit_id);
    const response = yield call(post, process.env.REACT_APP_GET_LEAVE_LIST, formData);

    if (!response || response.status !== 'success') {
      throw new Error(response?.message || 'Failed to fetch leave types!');
    }

    yield put({ type: types.FETCH_LEAVE_TYPE_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire('Error', error.message, 'error');
    yield put({ type: types.FETCH_LEAVE_TYPE_FAILURE, payload: error });
  }
}

// Leave Balance
function* fetchLeaveBalanceSaga(action) {
  try {
    const response = yield call(post, process.env.REACT_APP_GET_LEAVE_BALANCE_LIST, action.payload);

    if (!response || response.status !== 'success') {
      throw new Error(response?.message || 'Failed to fetch leave balance!');
    }

    yield put({ type: types.FETCH_LEAVE_BALANCE_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire('Error', error.message, 'error');
    yield put({ type: types.FETCH_LEAVE_BALANCE_FAILURE, payload: error });
  }
}

// Check Leave Exists
function* checkLeaveExistsSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_CHECK_LEAVE_EXISTS,
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to check leave exists!");
    }

    // ✅ check result inside response.data[0]
    const resultObj = response?.data?.[0];
    if (resultObj && resultObj.result > 0) {
      Swal.fire("Warning", resultObj.msg, "warning");
    }

    yield put({ type: types.CHECK_LEAVE_EXISTS_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.CHECK_LEAVE_EXISTS_FAILURE, payload: error });
  }
}

// Fetch Week Off / Holiday Dates
function* fetchWeekOfDatesSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_GET_WEEK_OF_DATES, // add in .env
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to fetch week off/holidays!");
    }

    const holidays = response?.data?.filter(d => d.holiday).map(d => d.holiday);
    const weekOffs = response?.data?.filter(d => d.week_off).map(d => d.week_off);

    yield put({ type: types.FETCH_WEEK_OF_DATES_SUCCESS, payload: {holidays,weekOffs} });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.FETCH_WEEK_OF_DATES_FAILURE, payload: error });
  }
}

function* applyTabSaga(action) {
  try {
    let apiUrl = "";
    switch (action.tabName) {
      case "Leave":
        apiUrl = process.env.REACT_APP_API_APPLY_LEAVE; // api/Attendance/ApplyLeave
        break;
      case "Permission":
        apiUrl = process.env.REACT_APP_API_APPLY_PERMISSION; // api/Attendance/ApplyPermission
        break;
      case "Onduty":
        apiUrl = process.env.REACT_APP_API_APPLY_ONDUTY; // api/Attendance/ApplyOnduty
        break;
      default:
        throw new Error("Invalid Tab Name");
    }

    const response = yield call(post, apiUrl, action.payload);

    if (!response || response.status !== "success") {
      throw new Error(response?.message || `Failed to apply ${action.tabName}`);
    }

    yield put({ type: types.APPLY_TAB_SUCCESS, payload: response });

    Swal.fire("Success", response?.message || `${action.tabName} applied successfully!`, "success");
  } catch (error) {
    yield put({ type: types.APPLY_TAB_FAILURE, payload: error });
    Swal.fire("Error", error.message || "Something went wrong!", "error");
  }
}

// 🔹 Fetch Marker Pending List
function* fetchMarkerPendingListSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_MARKER_PENDING_LIST, 
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to fetch marker pending list!");
    }

    yield put({ type: types.FETCH_MARKER_PENDING_LIST_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.FETCH_MARKER_PENDING_LIST_FAILURE, payload: error });
  }
}

// 🔹 Approve Marker
function* markerApproveSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_MARKER_APPROVE, // add to .env
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to approve marker!");
    }

    Swal.fire("Success", response?.message , "success");
    yield put({ type: types.MARKER_APPROVE_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.MARKER_APPROVE_FAILURE, payload: error });
  }
}

// 🔹 Fetch Marker Cancel List
function* fetchMarkerCancelListSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_MARKER_CANCEL_LIST, // add to .env
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to fetch marker cancel list!");
    }

    yield put({ type: types.FETCH_MARKER_CANCEL_LIST_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.FETCH_MARKER_CANCEL_LIST_FAILURE, payload: error });
  }
}

// 🔹 Cancel Marker
function* markerCancelSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_MARKER_CANCEL, // add to .env
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to cancel marker!");
    }

    Swal.fire("Success", response?.message , "success");
    yield put({ type: types.MARKER_CANCEL_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.MARKER_CANCEL_FAILURE, payload: error });
  }
}

// 🔹 Fetch Absent List
function* fetchAbsentListSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_GET_ABSENT_LIST, // add to .env
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to fetch absent list!");
    }

    yield put({ type: types.FETCH_ABSENT_LIST_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.FETCH_ABSENT_LIST_FAILURE, payload: error });
  }
}


//// 🔹 LOP Update
function* absentLopUpdateSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_ABSENT_LIST_LOP_UPDATE,
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to update LOP!");
    }

    Swal.fire("Success", response?.message || "LOP updated successfully!", "success");
    yield put({ type: types.ABSENT_LOP_UPDATE_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.ABSENT_LOP_UPDATE_FAILURE, payload: error });
  }
}

// 🔹 Leave Update
// function* absentLeaveUpdateSaga(action) {
//   try {
//     const response = yield call(
//       post,
//       process.env.REACT_APP_ABSENT_LIST_LEAVE_UPDATE,
//       action.payload
//     );

//     if (!response || response.status !== "success") {
//       throw new Error(response?.message || "Failed to update Leave!");
//     }

//     Swal.fire("Success", response?.message || "Leave updated successfully!", "success");
//     yield put({ type: types.ABSENT_LEAVE_UPDATE_SUCCESS, payload: response });
//   } catch (error) {
//     Swal.fire("Error", error.message, "error");
//     yield put({ type: types.ABSENT_LEAVE_UPDATE_FAILURE, payload: error });
//   }
// }

function* absentLeaveUpdateSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_ABSENT_LIST_LEAVE_UPDATE,
      action.payload
    );

    if (!response) {
      throw new Error("No response from server");
    }

    if (response.status === "failure") {
      yield put({
        type: types.ABSENT_LEAVE_UPDATE_FAILURE,
        payload: response,
      });

      return; // ⛔ stop execution
    }

    // ✅ SUCCESS CASE
    Swal.fire(
      "Success",
      response.message || "Leave updated successfully!",
      "success"
    );

    yield put({
      type: types.ABSENT_LEAVE_UPDATE_SUCCESS,
      payload: response,
    });

  } catch (error) {
    Swal.fire("Error", error.message || "Something went wrong", "error");

    yield put({
      type: types.ABSENT_LEAVE_UPDATE_FAILURE,
      payload: error,
    });
  }
}


// 🔹 OD Update
function* absentOdUpdateSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_ABSENT_LIST_OD_UPDATE,
      action.payload
    );

    if (!response || response.status !== "success") {
      throw new Error(response?.message || "Failed to update OD!");
    }

    Swal.fire("Success", response?.message || "OD updated successfully!", "success");
    yield put({ type: types.ABSENT_OD_UPDATE_SUCCESS, payload: response });
  } catch (error) {
    Swal.fire("Error", error.message, "error");
    yield put({ type: types.ABSENT_OD_UPDATE_FAILURE, payload: error });
  }
}

// 🔹 WeekOff Update
// function* absentWeekOffUpdateSaga(action) {
//   try {
//     const response = yield call(
//       post,
//       process.env.REACT_APP_ABSENT_LIST_WEEKOFF_UPDATE,
//       action.payload
//     );

//     if (!response || response.status !== "success") {
//       throw new Error(response?.message || "Failed to update WeekOff!");
//     }

//     Swal.fire("Success", response?.message || "WeekOff updated successfully!", "success");
//     yield put({ type: types.ABSENT_WEEKOFF_UPDATE_SUCCESS, payload: response });
//   } catch (error) {
//     Swal.fire("Error", error.message, "error");
//     yield put({ type: types.ABSENT_WEEKOFF_UPDATE_FAILURE, payload: error });
//   }
// }

function* absentWeekOffUpdateSaga(action) {
  try {
    const response = yield call(
      post,
      process.env.REACT_APP_ABSENT_LIST_WEEKOFF_UPDATE,
      action.payload
    );

    if (!response) {
      throw new Error("No response from server");
    }

    // ❌ BUSINESS FAILURE (handled by reducer + modal)
    if (response.status === "failure") {
      yield put({
        type: types.ABSENT_WEEKOFF_UPDATE_FAILURE,
        payload: response,
      });

      return; // ⛔ stop execution
    }

    // ✅ SUCCESS CASE
    Swal.fire(
      "Success",
      response.message || "WeekOff updated successfully!",
      "success"
    );

    yield put({
      type: types.ABSENT_WEEKOFF_UPDATE_SUCCESS,
      payload: response,
    });

  } catch (error) {
    Swal.fire("Error", error.message || "Something went wrong", "error");

    yield put({
      type: types.ABSENT_WEEKOFF_UPDATE_FAILURE,
      payload: error,
    });
  }
}

function* checkEmployeeLeaveTypeSaga(action) {
  try {
    const response = yield call(post, process.env.REACT_APP_EMPLOYEE_LEAVE_TYPE_REPORT, action.payload);

    if (!response || response.status !== "success") {
      yield put({ type: types.FETCH_EMPLOYEE_LEAVE_TYPE_SUCCESS, payload: { data: [] }, });
      return;
    }

    const resultObj = response?.data;

    if (!resultObj || resultObj.result <= 0) {
      yield put({ type: types.FETCH_EMPLOYEE_LEAVE_TYPE_SUCCESS, payload: { data: [] }, });
      return;
    }

    yield put({ type: types.FETCH_EMPLOYEE_LEAVE_TYPE_SUCCESS, payload: response, });

  } catch (error) {
    yield put({ type: types.FETCH_EMPLOYEE_LEAVE_TYPE_FAILURE, payload: error, });
  }
}


// Watchers
export default function* leaveSaga() {
  yield takeLatest(types.FETCH_EMPLOYEE_LIST_REQUEST, fetchEmployeeListSaga);
  yield takeLatest(types.FETCH_LEAVE_TYPE_REQUEST, fetchLeaveTypeSaga);
  yield takeLatest(types.FETCH_LEAVE_BALANCE_REQUEST, fetchLeaveBalanceSaga);
  yield takeLatest(types.CHECK_LEAVE_EXISTS_REQUEST, checkLeaveExistsSaga);
  yield takeLatest(types.FETCH_WEEK_OF_DATES_REQUEST, fetchWeekOfDatesSaga);
  yield takeLatest(types.APPLY_TAB_REQUEST, applyTabSaga);

  yield takeLatest(types.FETCH_MARKER_PENDING_LIST_REQUEST, fetchMarkerPendingListSaga);
  yield takeLatest(types.MARKER_APPROVE_REQUEST, markerApproveSaga);
  yield takeLatest(types.FETCH_MARKER_CANCEL_LIST_REQUEST, fetchMarkerCancelListSaga);
  yield takeLatest(types.MARKER_CANCEL_REQUEST, markerCancelSaga);

  yield takeLatest(types.FETCH_ABSENT_LIST_REQUEST, fetchAbsentListSaga);
  yield takeLatest(types.ABSENT_LOP_UPDATE_REQUEST, absentLopUpdateSaga);
  yield takeLatest(types.ABSENT_LEAVE_UPDATE_REQUEST, absentLeaveUpdateSaga);
  yield takeLatest(types.ABSENT_OD_UPDATE_REQUEST, absentOdUpdateSaga);
  yield takeLatest(types.ABSENT_WEEKOFF_UPDATE_REQUEST, absentWeekOffUpdateSaga);
  yield takeLatest(types.FETCH_EMPLOYEE_LEAVE_TYPE_REQUEST, checkEmployeeLeaveTypeSaga);
}
